import React, { useId, useRef, useState } from 'react';
import { Clock3 } from 'lucide-react';
import { getLanguage, t } from '../i18n';
import './EvaluationPanel.css';

const getGradeLabel = (grade) => t(`interface.assessment.gradeLabels.${grade}`);

function EvaluationPanel({
  onEvaluate,
  disabled = false,
  selectedGrade = 0,
  autoGradeCue = null,
  quickGradeLimitExpired = false,
  gradeUpdated = false,
}) {
  const timeoutId = useId();
  const submittingRef = useRef(false);
  const pointerFocusRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tempSelectedGrade, setTempSelectedGrade] = useState(null);
  const [hoverGrade, setHoverGrade] = useState(null);
  const [focusGrade, setFocusGrade] = useState(null);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const helpGrade = disabled || isSubmitting ? null : (focusGrade ?? hoverGrade);

  const handleGradeClick = async (grade) => {
    if (disabled || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setTempSelectedGrade(grade);
    setHoverGrade(null);
    setFocusGrade(null);
    setSubmissionFailed(false);
    try {
      await onEvaluate?.(grade);
    } catch {
      setSubmissionFailed(true);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      setTempSelectedGrade(null);
    }
  };

  const displayGrade = tempSelectedGrade ?? helpGrade ?? selectedGrade;
  // An event, not a change of selectedGrade, triggers feedback: entering another
  // card must never look like a new assessment. The event carries its resolved grade.
  const cueGrade = autoGradeCue?.grade ?? selectedGrade;
  const labelGrade = helpGrade ?? (autoGradeCue ? cueGrade : null);
  const feedbackVisible = submissionFailed || labelGrade !== null || Boolean(autoGradeCue?.message);

  return (
    <div className={['evaluation-panel', gradeUpdated ? 'grade-updated' : ''].filter(Boolean).join(' ')}
      role="group" aria-label={t('interface.assessment.recall')} aria-busy={isSubmitting}>
      {(quickGradeLimitExpired || isSubmitting) && (
        <div className="evaluation-prompt">
          <span className="rating-state-icons">
            {quickGradeLimitExpired && (
              <span id={timeoutId} className="rating-time-limit-indicator" role="img"
                title={t('interface.assessment.timeLimitExceeded')}
                aria-label={t('interface.assessment.timeLimitExceeded')}>
                <Clock3 size={14} aria-hidden="true" />
              </span>
            )}
            {isSubmitting && <span className="inline-spinner" role="status"
              aria-label={t('interface.assessment.saving')} />}
          </span>
        </div>
      )}
      <div className="star-rating-container">
        {[1, 2, 3, 4, 5].map((starValue) => (
          <button
            key={starValue}
            type="button"
            onClick={() => handleGradeClick(starValue)}
            onPointerEnter={(event) => {
              if (!disabled && event.pointerType === 'mouse') setHoverGrade(starValue);
            }}
            onPointerLeave={() => setHoverGrade(null)}
            onPointerDown={() => {
              pointerFocusRef.current = true;
              setFocusGrade(null);
            }}
            onFocus={() => {
              if (!disabled && !pointerFocusRef.current) setFocusGrade(starValue);
            }}
            onBlur={() => {
              pointerFocusRef.current = false;
              setFocusGrade(null);
              setHoverGrade(null);
            }}
            onKeyDown={(event) => {
              pointerFocusRef.current = false;
              if (event.key === 'Enter' || event.key === ' ') setFocusGrade(null);
            }}
            disabled={disabled || isSubmitting}
            className={['star-btn', starValue <= displayGrade ? 'filled' : 'empty',
              tempSelectedGrade === starValue ? 'selected' : '',
              quickGradeLimitExpired && starValue >= 4 ? 'quick-limit-muted' : '',
            ].filter(Boolean).join(' ')}
            aria-label={starValue + ' star' + (starValue > 1 ? 's' : '') + ' - ' + getGradeLabel(starValue)}
            aria-pressed={selectedGrade === starValue}
            aria-describedby={quickGradeLimitExpired && starValue >= 4 ? timeoutId : undefined}
          >
            {'\u2605'}
          </button>
        ))}
      </div>
      <div className="rating-label-container" role="status" aria-live="polite" aria-atomic="true">
        {feedbackVisible && (
          <div key={autoGradeCue?.id || (submissionFailed ? 'error' : 'help')}
            className={['rating-copy', submissionFailed ? 'rating-copy-error' : '',
              autoGradeCue && helpGrade === null && !submissionFailed ? 'rating-copy-event' : '',
            ].filter(Boolean).join(' ')}>
            {submissionFailed ? (
              <p className="rating-label">{t('interface.assessment.failed')}</p>
            ) : (
              <>
                {labelGrade !== null && <p className="rating-label" lang={getLanguage()}>{getGradeLabel(labelGrade)}</p>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EvaluationPanel;
